import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Descriptions, Drawer, Form, Input, Modal, Space, Spin, Table, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { useAccess } from '../../../auth/AccessContext.jsx';
import StatusTag from '../../../components/StatusTag.jsx';
import { ratePercent } from '../../../domain/feeRate.js';
import { formatInstant, formatMoney, formatUnits } from '../../../format';
import { STATUS_KIND } from '../../../status/statusHelp.js';
import { StepUpCancelled } from '../../../stepup/useStepUp.js';
import {
    CHECK_LABEL, OUTCOME_COLOUR, OVERRIDABLE_CHECK, OVERRIDE_REASON_MIN, SOURCE_LABEL, STATE_COLOUR, STATE_HINT, approveParams, bodyOf, checkRows, dayOnDay,
    isStale, unitsPendingCheck, overrideParams, personOf, rejectParams, runOffers, trailRows
} from './runs.js';

/** IP-203: ' (1%)' beside a fee price - the rate the run struck it at - or nothing for a run struck before 0038. */
const withRate = (rate) => (ratePercent(rate) ? ' (' + ratePercent(rate) + ')' : '');

const { Text } = Typography;

const dash = (value, format = (v) => v) => (value === null || value === undefined || value === '' ? '—' : format(value));

/** Who and when, for a leg of the run. */
const leg = (who, at) => (who ? who + (at ? ', ' + formatInstant(at) : '') : '—');

const checkColumns = [
    {
        title: 'Check',
        dataIndex: 'code',
        key: 'code',
        width: 260,
        render: (code) => <><Text strong>{CHECK_LABEL[code] ?? code}</Text><div><Text type="secondary" style={{ fontSize: 11.5 }}>{code}</Text></div></>
    },
    { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', width: 130, render: (v) => <StatusTag kind={STATUS_KIND.PRICE_CHECK} code={v} color={OUTCOME_COLOUR[v] ?? 'default'} /> },
    {
        title: 'Detail',
        key: 'detail',
        render: (_, c) => (
            <>
                {dash(c.detail)}
                {c.override && (
                    <div>
                        <Text type="secondary">
                            Overridden (was {c.override.was}) by {c.override.byName ?? c.override.by}
                            {c.override.at ? ', ' + formatInstant(c.override.at) : ''}: {c.override.reason}
                        </Text>
                    </div>
                )}
            </>
        )
    }
];

const trailColumns = [
    { title: 'When', dataIndex: 'occurredAt', key: 'occurredAt', width: 170, render: (v) => formatInstant(v) },
    { title: 'Who', dataIndex: 'who', key: 'who', width: 180 },
    { title: 'What', dataIndex: 'action', key: 'action', width: 220 },
    { title: 'Details', dataIndex: 'details', key: 'details', render: (v) => <Text type="secondary">{dash(v)}</Text> }
];

/**
 * One NAV run as GET pricing/runs/{id} answers it - { run, batch, price,
 * trail } - with the decisions the signed-in person may take on it (runs.js
 * runOffers). Each button is gated on exactly its action's claims
 * (SCREEN_RULES.unitPrices; auth/screenGates.registry.test.js), offered only
 * in the run's states that admit it, and - when four eyes stands in the way -
 * disabled with the reason as visible text. Rendered by RunDrawer, and on its
 * own by UnitPrices.test.js.
 */
export const RunDetail = ({ detail, busy = null, onOverride, onApprove, onReject }) => {
    const { canAct, principal } = useAccess();
    const canOverride = canAct('unitPrices', 'override');
    const canApprove = canAct('unitPrices', 'approve');
    const canReject = canAct('unitPrices', 'reject');

    const { run, batch = null, price = null, trail = [] } = detail;
    const offers = runOffers(run, { uid: principal?.uid, canOverride, canApprove, canReject });
    const blocked = [offers.approve.offered && offers.approve.blocked].filter(Boolean);
    const anyOffered = Object.values(offers).some((o) => o.offered);

    const figures = [
        { key: 'fund', label: 'Fund', children: <>{run.fundId}{run.lwCode ? <Text type="secondary"> ({run.lwCode})</Text> : null}</> },
        { key: 'date', label: 'Valuation date', children: run.valuationDate },
        { key: 'version', label: 'Version', children: run.version },
        { key: 'state', label: 'State', children: <><StatusTag kind={STATUS_KIND.NAV_RUN} code={run.state} color={STATE_COLOUR[run.state]} /><Text type="secondary">{STATE_HINT[run.state]}</Text></>, span: 3 },
        { key: 'nav', label: 'NAV (LKR)', children: dash(run.nav, formatMoney) },
        { key: 'units', label: 'Units in issue (the Core\'s)', children: dash(run.unitsInIssue, formatUnits) },
        { key: 'fileUnits', label: 'Units in the file', children: dash(run.fileIssuedUnits, formatUnits) },
        { key: 'navpu', label: 'NAV per unit', children: dash(run.navPerUnit, formatUnits) },
        { key: 'selling', label: 'Selling price', children: dash(run.sellingPrice, formatUnits) },
        { key: 'buying', label: 'Buying price', children: dash(run.buyingPrice, formatUnits) },
        // IP-203: each fee price with the rate it was struck at; a fund that charges no such fee has none.
        {
            key: 'bwef', label: 'Buying price with exit fee',
            children: run.buyingPriceWithExitFee ? formatUnits(run.buyingPriceWithExitFee) + withRate(run.exitFeeRate) : 'No exit fee'
        },
        {
            key: 'swef', label: 'Selling price with front-end fee',
            children: run.sellingPriceWithEntryFee ? formatUnits(run.sellingPriceWithEntryFee) + withRate(run.entryFeeRate) : 'No front-end fee'
        },
        { key: 'chargeBase', label: 'Charge base (LKR)', children: dash(run.chargeBase, formatMoney) },
        { key: 'cost', label: 'Transaction cost (LKR)', children: dash(run.transactionCost, formatMoney) },
        { key: 'prior', label: 'Prior NAV per unit', children: dash(run.priorNavPerUnit, formatUnits) },
        { key: 'dod', label: 'Day on day', children: dayOnDay(run) }
    ];

    const people = [
        { key: 'maker', label: 'Uploaded by (maker)', children: leg(personOf(run, 'makerUid'), run.createdAt) },
        ...(run.overrideBy ? [{ key: 'override', label: 'Check overridden by', children: leg(personOf(run, 'overrideBy')) + (run.overrideReason ? ': ' + run.overrideReason : '') }] : []),
        { key: 'approved', label: 'Approved by', children: leg(personOf(run, 'approvedBy'), run.approvedAt) },
        // A run published before migration 0016 also names its Trustee; no run is verified since.
        ...(run.verifiedBy ? [{ key: 'verified', label: 'Verified by (Trustee)', children: leg(personOf(run, 'verifiedBy'), run.verifiedAt) }] : []),
        ...(run.rejectedBy ? [{ key: 'rejected', label: 'Rejected by', children: leg(personOf(run, 'rejectedBy')) + (run.rejectReason ? ': ' + run.rejectReason : '') }] : [])
    ];

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            {anyOffered && (
                <Space wrap>
                    {/* IP-195: transactions dated before this date still to post hold the price - named one by one. */}
            {unitsPendingCheck(run) && (
                <Alert type="warning" showIcon className="auth-alert"
                    message="This price cannot be approved yet: transactions dated before it are still to post."
                    description={(
                        <>
                            <div>Approve or reject these transactions before approving this price: their units change the unit count the price is divided by. Then upload the NAV file again.</div>
                            <ul>{(unitsPendingCheck(run).pending ?? []).map((t) => <li key={t}>{t}</li>)}</ul>
                        </>
                    )} />
            )}
            {offers.override.offered && (
                        <Button icon={<SafetyOutlined />} disabled={Boolean(busy)} loading={busy === 'override'} onClick={onOverride}>Override</Button>
                    )}
                    {offers.approve.offered && (
                        <Button type="primary" icon={<SafetyOutlined />} disabled={Boolean(busy) || Boolean(offers.approve.blocked)} loading={busy === 'approve'}
                            onClick={onApprove} aria-describedby={offers.approve.blocked ? 'run-blocked' : undefined}>
                            Approve and publish
                        </Button>
                    )}
                    {offers.reject.offered && (
                        <Button danger icon={<SafetyOutlined />} disabled={Boolean(busy)} loading={busy === 'reject'} onClick={onReject}>Reject</Button>
                    )}
                    {/* Why a decision is off, as text: a disabled button takes no focus, so a tooltip on it is never read. */}
                    {blocked.length > 0 && <Text type="secondary" id="run-blocked">{blocked.join(' ')}</Text>}
                </Space>
            )}
            {offers.override.offered && (
                <Alert type="warning" showIcon className="auth-alert"
                    message={'Day-on-day failed: ' + (run.checks.find((c) => c.code === OVERRIDABLE_CHECK)?.detail ?? 'the move is outside the fund\'s band') + '.'}
                    description="Compliance may override it with a reason; the run then still needs another person's approval, which publishes the price. Otherwise reject it and upload a corrected file." />
            )}
            <Descriptions size="small" bordered column={3} title="The run" items={figures} />
            <Descriptions size="small" bordered column={1} title="People" items={people} />
            <Table rowKey="code" size="small" columns={checkColumns} dataSource={checkRows(run)} pagination={false} title={() => 'Checks'} />
            <Descriptions size="small" bordered column={2} title="The file it came from" items={batch ? [
                { key: 'file', label: 'File', children: batch.fileName, span: 2 },
                { key: 'date', label: 'Business date', children: dash(batch.businessDate) },
                { key: 'sha', label: 'SHA-256', children: batch.sha256 ? <Text code>{batch.sha256.slice(0, 16)}…</Text> : '—' },
                { key: 'uploaded', label: 'Uploaded by', children: dash(personOf(batch, 'uploadedBy')) },
                { key: 'accepted', label: 'Accepted by', children: leg(personOf(batch, 'acceptedBy'), batch.acceptedAt) }
            ] : [{ key: 'none', label: 'File', children: 'Not found' }]} />
            {price && (
                <Descriptions size="small" bordered column={2} title="The published price" items={[
                    { key: 'date', label: 'Effective date', children: price.dealingDate },
                    { key: 'version', label: 'Version', children: price.version },
                    { key: 'source', label: 'Source', children: SOURCE_LABEL[price.source] ?? dash(price.source) },
                    { key: 'at', label: 'Published', children: formatInstant(price.publishedAt) }
                ]} />
            )}
            <Table rowKey="key" size="small" columns={trailColumns} dataSource={trailRows(trail)} pagination={false} title={() => 'Trail'}
                locale={{ emptyText: 'Nothing decided on this run yet' }} />
        </div>
    );
};

/** What each decision's reason dialog says. */
const REASON = Object.freeze({
    override: {
        title: (run) => 'Override day-on-day for ' + run.fundId + ' on ' + run.valuationDate + '?',
        body: 'The check is recorded as overridden, with your reason, and the run becomes CHECKED: it still needs another person\'s approval, which publishes the price.',
        ok: 'Override',
        min: OVERRIDE_REASON_MIN,
        danger: false
    },
    reject: {
        title: (run) => 'Reject the NAV run for ' + run.fundId + ' on ' + run.valuationDate + '?',
        body: 'A rejected run publishes nothing. A corrected NAV file can then be uploaded for the date.',
        ok: 'Reject',
        min: 1,
        danger: true
    }
});

/**
 * The run's drawer: GET pricing/runs/{id}, and its four decisions - each on a
 * fresh authenticator code (withStepUp, the screen's one dialog), with the
 * params the catalogue binds (runs.js). Override and Reject ask for a reason
 * first. A refusal that says the page was stale (404, 409) closes the reason
 * dialog, is said on the drawer, and the run and the screen's lists are read
 * again (onDecided), so what the person sees next is what the server holds.
 * intent ('approve' | 'reject'), from a row's button on Unit
 * Prices, starts that decision as soon as the run is read.
 */
const RunDrawer = ({ navRunId, intent = null, onClose, withStepUp, onDecided }) => {
    const { api, canAct, principal } = useAccess();
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [reasonFor, setReasonFor] = useState(null); // 'override' | 'reject'
    const [reasonError, setReasonError] = useState(null);
    // The decision a row's button asked for, started once this run's detail is read.
    const pendingIntent = useRef(null);

    const load = useCallback(async () => {
        if (!navRunId) return;
        setLoading(true);
        try {
            setDetail(await api.pricing.run(navRunId));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api, navRunId]);

    useEffect(() => {
        setDetail(null);
        setError(null);
        setReasonFor(null);
        pendingIntent.current = intent;
        load();
    }, [load, intent]);

    const run = detail?.run ?? null;

    /** The reason dialog closes with its text cleared, so the next one opens empty. */
    const closeReason = () => {
        if (reasonFor) form.resetFields();
        setReasonFor(null);
    };

    /** One decision: its params (or why not), the code, the request; a stale refusal reloads. */
    const decide = async (kind, type, built, call, done) => {
        if (built.error) {
            if (reasonFor && built.field === 'reason') form.setFields([{ name: 'reason', errors: [built.error] }]);
            else (reasonFor ? setReasonError : setError)(built.error);
            return;
        }
        setBusy(kind);
        setError(null);
        setReasonError(null);
        try {
            await withStepUp(type, built.params, (stepUp) => call({ ...bodyOf(built.params), stepUp }));
            message.success(done);
            closeReason();
            load();
            onDecided();
        } catch (e) {
            if (e instanceof StepUpCancelled) return;
            if (isStale(e) || !reasonFor) {
                closeReason();
                setError(e.message);
                if (isStale(e)) {
                    load();
                    onDecided();
                }
            } else {
                setReasonError(e.message);
            }
        } finally {
            setBusy(null);
        }
    };

    const name = run ? run.fundId + ' on ' + run.valuationDate : '';
    const approve = () => decide('approve', 'PRICE_APPROVE', approveParams(run), (body) => api.pricing.approve(run.navRunId, body),
        'Approved and published: ' + name + '\'s price.');
    const submitReason = ({ reason }) => (reasonFor === 'override'
        ? decide('override', 'PRICE_OVERRIDE', overrideParams(run, reason), (body) => api.pricing.override(run.navRunId, body),
            'Day-on-day overridden on ' + name + '. Another person approves it next.')
        : decide('reject', 'PRICE_REJECT', rejectParams(run, reason), (body) => api.pricing.reject(run.navRunId, body),
            'Rejected: the NAV run for ' + name + '.'));
    const openReason = (kind) => {
        setError(null);
        setReasonError(null);
        setReasonFor(kind);
    };
    const spec = reasonFor ? REASON[reasonFor] : null;

    /*
     * A row's decision, started on the server's own read of the run: only when
     * it is still offered and not blocked by four eyes - otherwise the drawer
     * simply shows the run, and says why where it is blocked.
     */
    useEffect(() => {
        const wanted = pendingIntent.current;
        if (!wanted || !run || run.navRunId !== navRunId) return;
        pendingIntent.current = null;
        const offer = runOffers(run, {
            uid: principal?.uid, canOverride: false, canApprove: canAct('unitPrices', 'approve'),
            canReject: canAct('unitPrices', 'reject')
        })[wanted];
        if (!offer?.offered || offer.blocked) return;
        if (wanted === 'approve') approve();
        else if (wanted === 'reject') openReason('reject');
    }, [run]); // eslint-disable-line react-hooks/exhaustive-deps -- once per read of the run

    return (
        <Drawer open={Boolean(navRunId)} onClose={onClose} width={900} rootClassName="lfoc-styles-wrapper"
            title={run ? 'NAV run: ' + name + ' (version ' + run.version + ')' : 'NAV run'}>
            {error && <Alert type="error" showIcon message={error} className="auth-alert" closable onClose={() => setError(null)} />}
            {loading && !detail && <Spin />}
            {detail && (
                <RunDetail detail={detail} busy={busy} onOverride={() => openReason('override')} onApprove={approve}
                    onReject={() => openReason('reject')} />
            )}
            {spec && run && (
                <Modal
                    open
                    title={spec.title(run)}
                    okText={spec.ok}
                    okButtonProps={{ danger: spec.danger, loading: Boolean(busy), icon: <SafetyOutlined /> }}
                    cancelButtonProps={{ disabled: Boolean(busy) }}
                    onOk={() => form.submit()}
                    onCancel={closeReason}
                    maskClosable={false}
                    rootClassName="lfoc-styles-wrapper"
                >
                    <p>{spec.body} You will be asked for a code from your authenticator app.</p>
                    {reasonError && <Alert type="error" showIcon message={reasonError} className="auth-alert" />}
                    <Form form={form} layout="vertical" requiredMark={false} disabled={Boolean(busy)} onFinish={submitReason}>
                        <Form.Item name="reason" label="Reason"
                            extra={spec.min > 1 ? 'At least ' + spec.min + ' characters: it is kept with the run.' : undefined}
                            rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
                            <Input.TextArea maxLength={1000} autoSize={{ minRows: 2, maxRows: 5 }} />
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </Drawer>
    );
};

export default RunDrawer;
